import type { ButtonHTMLAttributes, Ref } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import type { IconName, IconSize, IconVariant } from '~/chords/Icon';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';
import { Tooltip } from '~/chords/Tooltip';

type Size = 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall';

const getButtonVariants = variants({
  base: cn(
    'flex items-center justify-center rounded-full transition duration-100 bg-transparent text-subtle',
    'hover:bg-emphasis-strong hover:text-hover active:bg-primary-default disabled:pointer-events-none disabled:bg-transparent disabled:text-icon-disabled',
  ),
  variants: {
    size: {
      xlarge: cn('size-[3rem] p-md'),
      large: cn('size-2xl p-sm'),
      medium: cn('size-xl p-sm'),
      small: cn('size-[1.75rem] p-xs'),
      xsmall: cn('size-[1.25rem]'),
    },
  },
});

const ICON_SIZE: Record<Size, IconSize> = {
  xlarge: 'lg',
  large: 'lg',
  medium: 'md',
  small: 'sm',
  xsmall: 'xs',
};

interface CopyButtonOwnProps {
  textToCopy: string;
  size?: Size;
  /** How long the copied state stays visible, in milliseconds. */
  tooltipDuration?: number;
  tooltipContent?: string;
  tooltipContentCopied?: string;
  /**
   * Show a tooltip. Defaults to true.
   * Independent of `showCheckmark`.
   */
  tooltip?: boolean;
  /**
   * Swap the icon for a check while a successful copy is visible.
   * A clipboard error keeps the copy icon. Independent of `tooltip`.
   */
  showCheckmark?: boolean;
}

export type CopyButtonProps = ComponentProps<
  CopyButtonOwnProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>
>;

/**
 * CopyButton Component
 *
 * A button component that allows users to copy a given text to the clipboard.
 * It displays a tooltip indicating the copy action was successful for a specified duration.
 *
 * @param textToCopy - The text string to be copied to the clipboard when the button is clicked.
 * @param size - Button size. One of `xsmall`, `small`, `medium`, `large`, or `xlarge`. Defaults to `medium`. The icon scales with the button.
 * @param tooltipDuration - The duration (in milliseconds) for which the "Copied" tooltip and optional check icon are displayed. Defaults to 1500ms.
 * @param tooltipContent - Optional idle tooltip text. Defaults to the translated "Copy text". Also used as the button's accessible name.
 * @param tooltipContentCopied - Optional tooltip text shown after a copy. Defaults to the translated "Copied". A clipboard error still shows the browser's error message.
 * @param tooltip - Show the tooltip. Defaults to true. Independent of `showCheckmark`.
 * @param showCheckmark - Swap the icon for a check while a successful copy is visible. A clipboard error keeps the copy icon. Defaults to false. Independent of `tooltip`.
 *
 * @returns A button with copy functionality and a tooltip indicating success.
 */
export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      textToCopy,
      size = 'medium',
      tooltipDuration = 1500,
      tooltipContent,
      tooltipContentCopied,
      tooltip = true,
      showCheckmark = false,
      dangerouslyOverrideClassName,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...props
    }: CopyButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const className = getClassNameOverride(
      { dangerouslyOverrideClassName },
      'CopyButton',
    );
    const { t } = useTranslation('chords');
    const [state, copyToClipboard] = useCopyToClipboard();
    const [copied, setCopied] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    const handleCopyClick = () => {
      copyToClipboard(textToCopy);

      setCopied(true);

      if (tooltip) {
        setTooltipOpen(true);
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        setTooltipOpen(false);
      }, tooltipDuration);
    };

    let tooltipLabel;

    if (state.error) {
      tooltipLabel = state.error.message;
    } else if (copied) {
      tooltipLabel = tooltipContentCopied || t('copyButton.copied');
    } else {
      tooltipLabel = tooltipContent || t('copyButton.copyText');
    }

    const iconParams = {
      type: (copied && showCheckmark && !state.error
        ? 'check'
        : 'copy') as IconName,
      variant: (isHovered ? 'solid' : 'regular') as IconVariant,
      size: ICON_SIZE[size],
    };

    const styles = getButtonVariants({ size });

    const button = (
      <button
        {...props}
        type="button"
        aria-label={tooltipContent || t('copyButton.copyText')}
        className={cn(styles, className)}
        ref={ref}
        onClick={(event) => {
          onClick?.(event);
          handleCopyClick();
        }}
        onMouseEnter={(event) => {
          setIsHovered(true);

          if (tooltip) {
            setTooltipOpen(true);
          }

          onMouseEnter?.(event);
        }}
        onMouseLeave={(event) => {
          setIsHovered(false);

          if (!copied) {
            setTooltipOpen(false);
          }

          onMouseLeave?.(event);
        }}
      >
        <Icon aria-hidden {...iconParams} />
      </button>
    );

    if (!tooltip) {
      return button;
    }

    return (
      <Tooltip
        behavior="passive"
        content={tooltipLabel}
        delayDuration={100}
        sideOffset={4}
        side="top"
        size="sm"
        open={tooltipOpen || copied}
        triggerAsChild
      >
        {button}
      </Tooltip>
    );
  },
);

CopyButton.displayName = 'CopyButton';
